import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import '../stylings/BucksCheckout.css';
import SquarePaymentForm from './SquarePaymentForm';
import OrderConfirmation from './OrderConfirmation';
import DeliveryAreaCheck from './DeliveryAreaCheck';
import { storeOrderData, getOrderData, clearOrderData } from '../utils/orderUtils';
import { sendOrderConfirmationEmail } from '../utils/emailUtils';

// Use environment variable instead of hardcoding - Updated from 10 to 7.5 miles
const DELIVERY_RADIUS_MILES = parseFloat(process.env.REACT_APP_DELIVERY_RADIUS_MILES) || 7.5; // Match with DeliveryAreaCheck component

// Add delivery minimum order constant
const DELIVERY_MINIMUM_ORDER = parseFloat(process.env.REACT_APP_DELIVERY_MINIMUM_ORDER) || 12.00;

// Add this constant for free delivery threshold
const FREE_DELIVERY_THRESHOLD = parseFloat(process.env.REACT_APP_FREE_DELIVERY_THRESHOLD) || 50.00;

// Helper functions for phone number formatting and validation
const formatPhoneNumber = (value) => {
  // Remove all non-numeric characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Apply formatting
  if (phoneNumber.length <= 3) {
    return phoneNumber;
  } else if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  } else {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  }
};

const validatePhoneNumber = (phone) => {
  // Remove all non-numeric characters
  const phoneNumber = phone.replace(/\D/g, '');
  
  // Check if it's a valid 10-digit US phone number
  return phoneNumber.length === 10 && /^\d{10}$/.test(phoneNumber);
};

function BucksCheckout({ initialOrderComplete, orderNumber: initialOrderNumber }) {
  const { 
    cartItems, 
    subtotal,
    calculateTotal,
    clearCart,
    isCartLoaded  // Add this line
  } = useCart();
  
  // Checkout form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: 'IL', // Changed from '' to 'IL'
    zipCode: '',
    paymentMethod: 'credit-card',
    specialInstructions: ''
  });
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  
  // Shipping method state
  const [shippingMethod, setShippingMethod] = useState('delivery');
  
  // Shipping fee calculation - Updated to use FREE_DELIVERY_THRESHOLD
  const shippingFee = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : 8.99;
  
  // Form steps - Updated to start at 0
  const [currentStep, setCurrentStep] = useState(0);
  const [orderComplete, setOrderComplete] = useState(initialOrderComplete || false);
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber || '');
  
  // Add new state for delivery area validation
  const [deliveryAreaValidated, setDeliveryAreaValidated] = useState(false);
  const [userAddress, setUserAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: ''
  });
  const [isInDeliveryArea, setIsInDeliveryArea] = useState(false);
  
  // Add state for order data
  const [orderData, setOrderData] = useState(null);
  
  // Payment processing state - Enhanced version with progress tracking
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState('ready'); // 'ready', 'validating', 'processing', 'storing', 'complete'
  const [paymentProgress, setPaymentProgress] = useState(0);
  
  // Add simplified payment form ready state
  const [isPaymentFormReady, setIsPaymentFormReady] = useState(false);
  
  // Create refs to store card and payment form for later use
  const cardRef = useRef(null);
  const paymentFormRef = useRef(null);
  
  // Check if there's saved order data on component mount
  useEffect(() => {
    if (initialOrderComplete || initialOrderNumber) {
      const savedOrderData = getOrderData();
      if (savedOrderData) {
        setOrderData(savedOrderData);
      } else {
        // Create basic order data if none exists but initialOrderComplete is true
        setOrderData({
          orderNumber: initialOrderNumber || 'Unknown',
          customerInfo: {
            name: 'Customer',
            email: 'email@example.com'
          },
          deliveryMethod: 'pickup',
          items: [],
          subtotal: 0,
          discount: 0,
          deliveryFee: 0,
          total: 0
        });
      }
      setOrderComplete(true);
    }
  }, [initialOrderComplete, initialOrderNumber]);
  
  // Auto-select appropriate shipping method when delivery area changes
  useEffect(() => {
    if (deliveryAreaValidated) {
      if (isInDeliveryArea && subtotal >= DELIVERY_MINIMUM_ORDER) {
        setShippingMethod('delivery'); // Auto-select delivery if in area and meets minimum
      } else {
        setShippingMethod('pickup');   // Auto-select pickup if outside area or below minimum
      }
    }
  }, [deliveryAreaValidated, isInDeliveryArea, subtotal]);
  
  // Handle card ready event from SquarePaymentForm - SIMPLIFIED VERSION
  const handleCardReady = useCallback((card, paymentForm) => {
    cardRef.current = card;
    paymentFormRef.current = paymentForm;
    setIsPaymentFormReady(true);
    
    console.log('Card is ready for payment processing');
  }, []);
  
  // Calculate total price
  const total = promoApplied 
    ? (subtotal * 0.9 + (shippingMethod === 'delivery' ? shippingFee : 0))
    : (subtotal + (shippingMethod === 'delivery' ? shippingFee : 0));
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Special handling for phone number
    if (name === 'phone') {
      processedValue = formatPhoneNumber(value);
    }
    
    setFormData({
      ...formData,
      [name]: name === 'state' ? 'IL' : (type === 'checkbox' ? checked : processedValue)
    });
  };
  
  // Apply promo code
  const applyPromoCode = (e) => {
    if (e) e.preventDefault();
    if (promoCode.toLowerCase() === 'harvest') {
      setPromoApplied(true);
    } else {
      alert('Invalid promo code. Try "HARVEST" for this demo.');
    }
  };
  
  // Go to next step - SIMPLIFIED VERSION
  const nextStep = async () => {
    // Basic validation for payment step
    if (currentStep === 2) {
      if (!isPaymentFormReady) {
        alert('Please wait for the payment form to load completely.');
        return;
      }
    }
    
    setCurrentStep(currentStep + 1);
    // Scroll to top when moving to next step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Go to previous step
  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    // Scroll to top when moving to previous step
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Submit checkout form
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // For steps 0, 1 and 2, just continue to the next step
    if (currentStep < 3) {
      nextStep();
      return;
    }
  };
  
  // Enhanced payment processing with progress indicators
  const handleProcessPayment = async () => {
    if (!cardRef.current) {
      alert('Payment form not initialized. Please return to the Payment step.');
      return;
    }
    
    // VALIDATE ALL DATA BEFORE PROCESSING PAYMENT
    const validationErrors = [];
    
    // Validate required fields
    if (!formData.firstName.trim()) validationErrors.push('First name is required');
    if (!formData.lastName.trim()) validationErrors.push('Last name is required');
    if (!formData.email.trim()) validationErrors.push('Email is required');
    if (!formData.phone.trim()) validationErrors.push('Phone number is required');
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      validationErrors.push('Please enter a valid email address');
    }
    
    // Validate phone number
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      validationErrors.push('Please enter a valid 10-digit phone number');
    }
    
    // Validate delivery address if delivery is selected
    if (shippingMethod === 'delivery') {
      if (!formData.address.trim()) validationErrors.push('Street address is required for delivery');
      if (!formData.city.trim()) validationErrors.push('City is required for delivery');
      if (!formData.zipCode.trim()) validationErrors.push('ZIP code is required for delivery');
      
      // Validate ZIP code format
      if (formData.zipCode && !/^\d{5}$/.test(formData.zipCode)) {
        validationErrors.push('Please enter a valid 5-digit ZIP code');
      }
      
      // Validate delivery minimum
      if (subtotal < DELIVERY_MINIMUM_ORDER) {
        validationErrors.push(`Delivery requires a minimum order of $${DELIVERY_MINIMUM_ORDER.toFixed(2)}`);
      }
    }
    
    // Show validation errors if any
    if (validationErrors.length > 0) {
      alert('Please fix the following errors:\n\n' + validationErrors.join('\n'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Start payment processing flow
    setIsProcessingPayment(true);
    setPaymentStep('validating');
    setPaymentProgress(10);
    
    try {
      // Step 1: Generate payment token
      console.log('Generating payment token...');
      const result = await cardRef.current.tokenize();
      
      if (result.status !== 'OK') {
        throw new Error(result.errors[0].message || 'Card validation failed');
      }
      
      setPaymentProgress(30);
      setPaymentStep('processing');
      
      // Clean phone number for submission
      const cleanPhone = formData.phone.replace(/\D/g, '');
      
      // Prepare optimized order data
      const orderDataForSubmission = {
        sourceId: result.token,
        amount: total,
        orderData: {
          customerName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
          email: formData.email.trim(),
          phone: cleanPhone,
          deliveryMethod: shippingMethod,
          address: shippingMethod === 'delivery' ? {
            line1: formData.address.trim(),
            line2: '',
            city: formData.city.trim(),
            state: formData.state,
            zipCode: formData.zipCode.trim()
          } : null,
          items: cartItems.map(item => ({
            productId: item.id,
            name: item.name,
            unit: item.unit || 'each',
            quantity: item.quantity,
            unitPrice: item.effectiveUnitPrice || item.price,
            totalPrice: (item.effectiveUnitPrice || item.price) * item.quantity
          })),
          subtotal: subtotal,
          promoCode: promoApplied ? promoCode : '',
          discountAmount: promoApplied ? (subtotal * 0.10) : 0,
          deliveryFee: shippingMethod === 'delivery' ? shippingFee : 0,
          total: total,
          specialInstructions: formData.specialInstructions || ''
        }
      };
      
      // Step 2: Process payment with timeout
      const paymentPromise = fetch('https://s9l70ztz3c.execute-api.us-east-1.amazonaws.com/default/bucks-customer-order-place-process-post', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderDataForSubmission),
      });
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Payment processing timeout')), 30000)
      );
      
      const response = await Promise.race([paymentPromise, timeoutPromise]);
      setPaymentProgress(60);
      
      const paymentResponse = await response.json();
      
      if (!paymentResponse.success) {
        throw new Error(paymentResponse.error || 'Payment failed');
      }
      
      setPaymentProgress(80);
      setPaymentStep('storing');
      
      // Step 3: Complete order processing
      await handlePaymentComplete({
        success: true,
        orderId: paymentResponse.orderId,
        orderNumber: paymentResponse.orderNumber,
        transactionId: paymentResponse.transactionId
      });
      
    } catch (error) {
      console.error('Payment processing error:', error);
      setIsProcessingPayment(false);
      setPaymentStep('ready');
      setPaymentProgress(0);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'An error occurred during payment processing';
      alert(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Function to handle payment completion and store order in database
  const handlePaymentComplete = async (paymentResult) => {
    if (paymentResult.success) {
      try {
        // Clean phone number for submission
        const cleanPhone = formData.phone.replace(/\D/g, '');
        
        // Call Lambda endpoint to store the order in the database
        const orderResponse = await fetch('https://s9351aaac7.execute-api.us-east-1.amazonaws.com/default/bucks-customer-order-store', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            transactionId: paymentResult.transactionId,
            orderData: {
              customerName: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
              email: formData.email.trim(),
              phone: cleanPhone, // Use cleaned phone number
              deliveryMethod: shippingMethod,
              address: shippingMethod === 'delivery' ? {
                line1: formData.address.trim(),
                line2: '', // Add this if you have a separate address line 2 field
                city: formData.city.trim(),
                state: formData.state,
                zipCode: formData.zipCode.trim()
              } : null,
              items: cartItems.map(item => ({
                productId: item.id,
                name: item.name,
                unit: item.unit || 'each', // Include unit information
                quantity: item.quantity,
                unitPrice: item.effectiveUnitPrice || item.price, // Use effective price for special pricing
                totalPrice: (item.effectiveUnitPrice || item.price) * item.quantity
              })),
              subtotal: subtotal,
              promoCode: promoApplied ? promoCode : '',
              discountAmount: promoApplied ? (subtotal * 0.10) : 0,
              deliveryFee: shippingMethod === 'delivery' ? shippingFee : 0,
              total: total,
              specialInstructions: formData.specialInstructions || ''
            }
          })
        });
        
        const orderResult = await orderResponse.json();
        
        if (orderResult.success) {
          // Use the order number from our database
          const newOrderNumber = orderResult.orderNumber || paymentResult.orderNumber || ('BF-' + Math.floor(100000 + Math.random() * 900000));
          setOrderNumber(newOrderNumber);
          
          // Create and store complete order data
          const completeOrderData = {
            orderNumber: newOrderNumber,
            transactionId: paymentResult.transactionId,
            customerInfo: {
              name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
              email: formData.email.trim(),
              phone: formData.phone, // Keep formatted phone for display
              address: shippingMethod === 'delivery' ? {
                line1: formData.address.trim(),
                city: formData.city.trim(),
                state: formData.state,
                zipCode: formData.zipCode.trim()
              } : null
            },
            deliveryMethod: shippingMethod,
            items: cartItems.map(item => ({
              id: item.id,
              name: item.name,
              unit: item.unit || 'each', // Include unit information
              quantity: item.quantity,
              unitPrice: item.effectiveUnitPrice || item.price, // Use effective price for special pricing
              totalPrice: (item.effectiveUnitPrice || item.price) * item.quantity
            })),
            subtotal: subtotal,
            discount: promoApplied ? (subtotal * 0.10) : 0,
            promoCode: promoApplied ? promoCode : '',
            deliveryFee: shippingMethod === 'delivery' ? shippingFee : 0, // Only include delivery fee if delivery method
            total: total,
            specialInstructions: formData.specialInstructions || '',
            dateCreated: new Date().toISOString()
          };
          
          // Store order data and update state
          storeOrderData(completeOrderData);
          setOrderData(completeOrderData);
          
          // Send order confirmation email
          try {
            const emailSent = await sendOrderConfirmationEmail(completeOrderData);
            if (!emailSent) {
              console.warn('Order confirmation email could not be sent.');
              // Continue with checkout even if email fails - don't block the user
            }
          } catch (emailError) {
            console.error('Error in email sending process:', emailError);
            // Continue with checkout even if email fails
          }
          
          // Show order confirmation
          setOrderComplete(true);
          
          // Scroll to top when showing order confirmation
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          // Update URL to include order number without page refresh
          const url = new URL(window.location);
          url.pathname = '/order-confirmation';
          url.searchParams.set('order', newOrderNumber);
          window.history.pushState({}, '', url);
          
          // Clear cart after successful order (from the second file)
          setTimeout(() => {
            clearCart();
          }, 1000);
        } else {
          // Handle order storage failure
          console.error('Order storage failed:', orderResult.error);
          alert('Your payment was processed, but there was an issue storing your order. Please contact customer support with reference: ' + paymentResult.transactionId);
          setIsProcessingPayment(false);
          setPaymentStep('ready');
          setPaymentProgress(0);
          // Scroll to top to show error message
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        // Handle any exceptions during order storage
        console.error('Error storing order:', error);
        alert('Your payment was processed, but there was an issue storing your order. Please contact customer support with reference: ' + paymentResult.transactionId);
        setIsProcessingPayment(false);
        setPaymentStep('ready');
        setPaymentProgress(0);
        // Scroll to top to show error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // Handle payment failure
      alert('Payment failed: ' + (paymentResult.error || 'Unknown error'));
      setIsProcessingPayment(false);
      setPaymentStep('ready');
      setPaymentProgress(0);
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // Check if cart is empty - but only after it's loaded
  const isCartEmpty = cartItems.length === 0;

  // Show loading state while cart is loading
  if (!isCartLoaded) {
    return (
      <div className="bucks-checkout">
        <div className="checkout-container">
          <div className="checkout-loading">
            <div className="loading-spinner"></div>
            <h2>Loading checkout...</h2>
            <p>Please wait while we prepare your order.</p>
          </div>
        </div>
      </div>
    );
  }

  // Return to basket if cart is empty and not viewing a completed order
  if (isCartEmpty && !orderComplete) {
    window.location.href = '/basket';
    return null;
  }
  
  // If order is complete, show the OrderConfirmation component
  if (orderComplete) {
    return <OrderConfirmation orderData={orderData} />;
  }

  return (
    <div className="bucks-checkout">
      <div className="checkout-container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <div className="checkout-steps">
            <div className={`step ${currentStep >= 0 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <span className="step-name">Method</span>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <span className="step-name">Details</span>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <span className="step-name">Payment</span>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">4</div>
              <span className="step-name">Review</span>
            </div>
          </div>
        </div>
        
        <div className="checkout-content">
          <div className="checkout-form-container">
            <form className="checkout-form" onSubmit={handleSubmit}>
              {/* Step 0: Delivery Area Check / Method Selection */}
              {currentStep === 0 && (
                <DeliveryAreaCheck
                  onAreaValidated={(isValid, addressData, selectedMethod) => {
                    setDeliveryAreaValidated(true);
                    setIsInDeliveryArea(isValid);
                    
                    // Handle method selection based on user choice or delivery area validation
                    if (selectedMethod === 'pickup') {
                      // User explicitly chose pickup
                      setShippingMethod('pickup');
                      console.log('User selected farmstand pickup - bypassing delivery check');
                    } else if (selectedMethod === 'delivery') {
                      // User chose delivery and we validated their address
                      if (isValid && subtotal >= DELIVERY_MINIMUM_ORDER) {
                        setShippingMethod('delivery'); // Address is in delivery area and meets minimum
                      } else {
                        setShippingMethod('pickup'); // Address outside delivery area or below minimum, fallback to pickup
                      }
                      
                      // Pre-fill form data with validated address if available
                      if (addressData) {
                        setUserAddress(addressData);
                        setFormData(prev => ({
                          ...prev,
                          address: addressData.street,
                          city: addressData.city,
                          state: 'IL',
                          zipCode: addressData.zipCode
                        }));
                      }
                    }
                    
                    // Always allow to continue
                    nextStep();
                  }}
                  onBack={() => window.location.href = '/basket'}
                />
              )}

              {/* Step 1: Contact & Shipping */}
              {currentStep === 1 && (
                <div className="checkout-step">
                  {/* Show appropriate message based on delivery area status and method */}
                  {deliveryAreaValidated && (
                    <>
                      {shippingMethod === 'pickup' && !isInDeliveryArea ? (
                        <div className="method-confirmation pickup-confirmation">
                          <div className="confirmation-icon">🏪</div>
                          <div className="confirmation-content">
                            <h3>Farmstand Pickup Selected</h3>
                            <p>Perfect! Your order will be ready for pickup at our farmstand tomorrow by 1:00 PM. Please bring a valid ID and your email order receipt when picking up your order.</p>
                            <div className="pickup-details">
                              <p><strong>Pickup Location:</strong> Buck's Farmstand</p>
                              <p><strong>Address:</strong> 4250 W 123rd St, Alsip, IL 60803</p>
                              <p><strong>Hours:</strong> Ready by 1:00 PM next day, call ahead and confirm if wanted</p>
                            </div>
                          </div>
                        </div>
                      ) : shippingMethod === 'pickup' && isInDeliveryArea ? (
                        <div className="method-confirmation pickup-confirmation">
                          <div className="confirmation-icon">🏪</div>
                          <div className="confirmation-content">
                            <h3>Farmstand Pickup Selected</h3>
                            <p>Great choice! Even though we can deliver to your area, pickup is always free and your order will be ready tomorrow by 1:00 PM.</p>
                          </div>
                        </div>
                      ) : !isInDeliveryArea ? (
                        <div className="delivery-warning">
                          <div className="warning-icon">⚠️</div>
                          <div className="warning-content">
                            <h3>Outside Delivery Area</h3>
                            <p>Your address is outside our delivery area. You can still place an order for farmstand pickup! Orders placed today will be ready for pickup tomorrow until 1:00 PM.</p>
                          </div>
                        </div>
                      ) : (
                        <div className="delivery-success">
                          <div className="success-icon">✅</div>
                          <div className="success-content">
                            <h3>Great! We Can Deliver to You</h3>
                            <p>Your address is within our delivery area. Home delivery has been automatically selected for your convenience. Orders placed today will be delivered tomorrow by 3:30 PM.</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className={`contact-section ${shippingMethod === 'pickup' ? 'pickup-centered' : ''}`}>
                    <h2>Contact Information</h2>
                    
                    <div className="form-group-row">
                      <div className="form-group">
                        <label htmlFor="firstName">First Name*</label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="lastName">Last Name*</label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="form-group-row">
                      <div className="form-group">
                        <label htmlFor="email">Email Address*</label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="phone">Phone Number*</label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          maxLength="14"
                          required
                        />
                        {formData.phone && !validatePhoneNumber(formData.phone) && (
                          <div style={{color: '#dc3545', fontSize: '0.8rem', marginTop: '0.3rem'}}>
                            Please enter a valid 10-digit phone number
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="delivery-options">
                    <h3>Delivery Options</h3>
                    
                    {/* Show minimum order warning if delivery is selected but minimum not met */}
                    {shippingMethod === 'delivery' && subtotal < DELIVERY_MINIMUM_ORDER && (
                      <div className="delivery-minimum-warning">
                        <div className="warning-icon">⚠️</div>
                        <div className="warning-content">
                          <h4>Delivery Minimum Not Met</h4>
                          <p>
                            Delivery requires a minimum order of <strong>${DELIVERY_MINIMUM_ORDER.toFixed(2)}</strong>. 
                            Your current order is <strong>${subtotal.toFixed(2)}</strong>. 
                            You need <strong>${(DELIVERY_MINIMUM_ORDER - subtotal).toFixed(2)}</strong> more to qualify for delivery.
                          </p>
                          <div className="warning-actions">
                            <button 
                              type="button"
                              className="add-items-warning-btn" 
                              onClick={() => window.location.href = '/shop'}
                            >
                              Add More Items
                            </button>
                            <button 
                              type="button"
                              className="switch-pickup-btn" 
                              onClick={() => setShippingMethod('pickup')}
                            >
                              Switch to Pickup
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="shipping-options">
                      <label className={`shipping-option ${
                        (!isInDeliveryArea || subtotal < DELIVERY_MINIMUM_ORDER)
                          ? 'disabled' 
                          : (isInDeliveryArea && shippingMethod === 'delivery' ? 'auto-selected' : '')
                      }`}>
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="delivery"
                          checked={shippingMethod === 'delivery' && isInDeliveryArea && subtotal >= DELIVERY_MINIMUM_ORDER}
                          onChange={() => {
                            if (subtotal >= DELIVERY_MINIMUM_ORDER) {
                              setShippingMethod('delivery')
                            }
                          }}
                          disabled={!isInDeliveryArea || subtotal < DELIVERY_MINIMUM_ORDER}
                        />
                        <div className="option-details">
                          <span className="option-name">
                            Home Delivery
                            {!isInDeliveryArea && <span className="unavailable">(Not Available in Your Area)</span>}
                            {subtotal < DELIVERY_MINIMUM_ORDER && isInDeliveryArea && 
                              <span className="unavailable">(Minimum ${DELIVERY_MINIMUM_ORDER.toFixed(2)} Required)</span>
                            }
                            {isInDeliveryArea && shippingMethod === 'delivery' && subtotal >= DELIVERY_MINIMUM_ORDER && 
                              <span className="auto-selected-badge">Auto-Selected</span>
                            }
                          </span>
                          <span className="option-price">
                            {shippingFee === 0 
                              ? `Free delivery (${subtotal >= FREE_DELIVERY_THRESHOLD ? 'qualified!' : `orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+`})` 
                              : `$${shippingFee.toFixed(2)} (free on orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+)`
                            }
                          </span>
                          {!isInDeliveryArea && (
                            <span className="option-note">Outside {DELIVERY_RADIUS_MILES}-mile delivery radius</span>
                          )}
                          {subtotal < DELIVERY_MINIMUM_ORDER && isInDeliveryArea && (
                            <span className="option-note">Need ${(DELIVERY_MINIMUM_ORDER - subtotal).toFixed(2)} more for delivery</span>
                          )}
                          {isInDeliveryArea && formData.address && subtotal >= DELIVERY_MINIMUM_ORDER && (
                            <span className="option-note">Your address: {formData.address}, {formData.city}, {formData.state} {formData.zipCode}</span>
                          )}
                        </div>
                      </label>
                      
                      <label className={`shipping-option ${shippingMethod === 'pickup' ? 'selected' : ''} ${(!isInDeliveryArea || subtotal < DELIVERY_MINIMUM_ORDER) ? 'recommended' : ''}`}>
                        <input
                          type="radio"
                          name="shippingMethod"
                          value="pickup"
                          checked={shippingMethod === 'pickup'}
                          onChange={() => setShippingMethod('pickup')}
                        />
                        <div className="option-details">
                          <span className="option-name">
                            Farmstand Pickup
                            {(!isInDeliveryArea || subtotal < DELIVERY_MINIMUM_ORDER) && <span className="recommended-badge">Recommended</span>}
                            {shippingMethod === 'pickup' && <span className="selected-badge">Selected</span>}
                          </span>
                          <span className="option-price">Free</span>
                          <span className="option-note">4250 W 123rd St, Alsip, IL 60803 - Ready by 1:00 PM</span>
                          <span className="option-note">No minimum order required</span>
                        </div>
                      </label>
                    </div>
                  </div>
                  
                  {shippingMethod === 'delivery' && (
                    <div className="delivery-address">
                      <h3>Delivery Address</h3>
                      
                      <div className="form-group">
                        <label htmlFor="address">Street Address*</label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          required={shippingMethod === 'delivery'}
                        />
                      </div>
                      
                      <div className="form-group-row">
                        <div className="form-group">
                          <label htmlFor="city">City*</label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={formData.city}
                            onChange={handleInputChange}
                            required={shippingMethod === 'delivery'}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="state">State*</label>
                          <input
                            type="text"
                            id="state"
                            name="state"
                            value="Illinois"
                            disabled
                            className="disabled-input"
                            style={{
                              backgroundColor: '#f8f9fa',
                              color: '#6c757d',
                              cursor: 'not-allowed'
                            }}
                          />
                        </div>
                        <div className="form-group">
                          <label htmlFor="zipCode">ZIP Code*</label>
                          <input
                            type="text"
                            id="zipCode"
                            name="zipCode"
                            value={formData.zipCode}
                            onChange={handleInputChange}
                            required={shippingMethod === 'delivery'}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="form-actions">
                    <button type="button" className="back-btn" onClick={prevStep}>Back to Method</button>
                    <button type="button" className="next-btn" onClick={nextStep}>Continue to Payment</button>
                  </div>
                </div>
              )}
              
              {/* Step 2: Payment - SIMPLIFIED VERSION */}
              {currentStep === 2 && (
                <div className="checkout-step">
                  <h2>Payment Method</h2>
                  
                  <div className="payment-methods">
                    <label className="payment-method">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="credit-card"
                        checked={true}
                        readOnly
                      />
                      <div className="method-details">
                        <span className="method-name">Credit / Debit Card</span>
                        <div className="card-icons">
                          <span className="card-icon">💳</span>
                        </div>
                      </div>
                    </label>
                  </div>
                  
                  <div className="payment-section">
                    <SquarePaymentForm 
                      showPayButton={false}
                      onCardReady={handleCardReady}
                      total={total}
                      orderData={{
                        customerName: `${formData.firstName} ${formData.lastName}`,
                        email: formData.email,
                        phone: formData.phone,
                        deliveryMethod: shippingMethod,
                        address: shippingMethod === 'delivery' ? {
                          line1: formData.address,
                          city: formData.city,
                          state: formData.state,
                          zipCode: formData.zipCode
                        } : null,
                        items: cartItems.map(item => ({
                          productId: item.id,
                          name: item.name,
                          unit: item.unit || 'each',
                          quantity: item.quantity,
                          unitPrice: item.effectiveUnitPrice || item.price,
                          totalPrice: (item.effectiveUnitPrice || item.price) * item.quantity
                        })),
                        subtotal: subtotal,
                        discountAmount: promoApplied ? (subtotal * 0.10) : 0,
                        deliveryFee: shippingMethod === 'delivery' ? shippingFee : 0,
                        total: total,
                        specialInstructions: formData.specialInstructions || ''
                      }}
                    />
                    
                    {/* Simplified payment status indicator */}
                    <div className="payment-status-indicator">
                      {!isPaymentFormReady ? (
                        <div className="status-loading">
                          <span className="status-icon">⏳</span>
                          <span>Loading payment form...</span>
                        </div>
                      ) : (
                        <div className="status-success">
                          <span className="status-icon">✅</span>
                          <span>Payment form ready - Square will validate your information</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" className="back-btn" onClick={prevStep}>Back to Details</button>
                    <button 
                      type="button" 
                      className="next-btn" 
                      onClick={nextStep}
                      disabled={!isPaymentFormReady}
                    >
                      Continue to Review
                    </button>
                  </div>
                </div>
              )}
              
              {/* Step 3: Review Order */}
              {currentStep === 3 && (
                <div className="checkout-step">
                  <h2>Review Your Order</h2>
                  
                  <div className="review-section">
                    <div className="review-section-header">
                      <h3>Contact Information</h3>
                      <button type="button" className="edit-btn" onClick={() => setCurrentStep(1)}>Edit</button>
                    </div>
                    <div className="review-details">
                      <p><strong>Name:</strong> {formData.firstName} {formData.lastName}</p>
                      <p><strong>Email:</strong> {formData.email}</p>
                      <p><strong>Phone:</strong> {formData.phone}</p>
                    </div>
                  </div>
                  
                  <div className="review-section">
                    <div className="review-section-header">
                      <h3>{shippingMethod === 'delivery' ? 'Delivery' : 'Pickup'} Method</h3>
                      <button type="button" className="edit-btn" onClick={() => setCurrentStep(1)}>Edit</button>
                    </div>
                    <div className="review-details">
                      <p><strong>Method:</strong> {shippingMethod === 'delivery' ? 'Home Delivery' : 'Farmstand Pickup'}</p>
                      {shippingMethod === 'delivery' && (
                        <p><strong>Address:</strong> {formData.address}, {formData.city}, {formData.state} {formData.zipCode}</p>
                      )}
                      {shippingMethod === 'pickup' && (
                        <p><strong>Pickup Location:</strong> Buck's Farmstand, 4250 W 123rd St, Alsip, IL 60803 (Ready by 1:00 PM)</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="review-section">
                    <div className="review-section-header">
                      <h3>Payment Method</h3>
                      <button type="button" className="edit-btn" onClick={() => setCurrentStep(2)}>Edit</button>
                    </div>
                    <div className="review-details">
                      <p><strong>Method:</strong> Credit Card</p>
                    </div>
                  </div>
                  
                  <div className="promo-section">
                    {/* Using div instead of form to prevent nested forms */}
                    <div className="promo-form">
                      <input
                        type="text"
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="promo-input"
                        disabled={promoApplied}
                      />
                      <button 
                        type="button" 
                        className="promo-btn"
                        onClick={applyPromoCode}
                        disabled={promoApplied}
                      >
                        {promoApplied ? 'Applied' : 'Apply'}
                      </button>
                    </div>
                    {promoApplied && (
                      <div className="promo-success">
                        <span>✓</span> HARVEST promo code applied!
                      </div>
                    )}
                  </div>
                  
                  <div className="order-review">
                    <h3>Order Items</h3>
                    <ul className="review-items">
                      {cartItems.map(item => (
                        <li key={item.id} className="review-item">
                          <div className="item-image-container">
                            <img src={item.image} alt={item.name} className="item-image" />
                          </div>
                          <div className="item-details">
                            <span className="item-name">{item.name}</span>
                            <span className="item-quantity">Quantity: {item.quantity}</span>
                          </div>
                          <span className="item-price">${((item.effectiveUnitPrice || item.price) * item.quantity).toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="order-totals">
                    <div className="totals-line">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    
                    {promoApplied && (
                      <div className="totals-line discount">
                        <span>Discount (10%)</span>
                        <span>-${(subtotal * 0.10).toFixed(2)}</span>
                      </div>
                    )}
                    
                    {shippingMethod === 'delivery' && (
                      <div className="totals-line">
                        <span>Delivery</span>
                        <span>{shippingFee === 0 ? 'Free' : `$${shippingFee.toFixed(2)}`}</span>
                      </div>
                    )}
                    
                    <div className="totals-line total">
                      <span>Total</span>
                      <span>${total.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  <div className="terms-agreement">
                    <p>By placing your order, you agree to Buck's Farmstand's <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.</p>
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" className="back-btn" onClick={prevStep}>Back to Payment</button>
                    <button 
                      type="button" 
                      className="place-order-btn"
                      onClick={handleProcessPayment}
                      disabled={isProcessingPayment}
                    >
                      {isProcessingPayment ? 'Processing Payment...' : 'Place Order & Pay'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
          
          <div className="checkout-summary">
            <div className="summary-header">
              <h2>Order Summary</h2>
              <a href="/basket" className="edit-cart-link">Edit Basket</a>
            </div>
            
            <div className="summary-items">
              {cartItems.map(item => (
                <div key={item.id} className="summary-item">
                  <div className="summary-item-image">
                    <img src={item.image} alt={item.name} />
                    <span className="summary-item-quantity">{item.quantity}</span>
                  </div>
                  <div className="summary-item-details">
                    <span className="summary-item-name">{item.name}</span>
                    <span className="summary-item-price">${((item.effectiveUnitPrice || item.price) * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="summary-totals">
              <div className="summary-line">
                <span>Subtotal</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              
              {promoApplied && (
                <div className="summary-line discount">
                  <span>Discount (10%)</span>
                  <span>-${(subtotal * 0.10).toFixed(2)}</span>
                </div>
              )}
              
              {shippingMethod === 'delivery' && (
                <div className="summary-line">
                  <span>Delivery</span>
                  <span>{shippingFee === 0 ? 'Free' : `$${shippingFee.toFixed(2)}`}</span>
                </div>
              )}
              
              <div className="summary-line total">
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="trust-badges">
              <div className="trust-badge">
                <span className="trust-icon">🌱</span>
                <span>Farm Fresh Guarantee</span>
              </div>
              <div className="trust-badge">
                <span className="trust-icon">🔒</span>
                <span>Secure Checkout</span>
              </div>
              <div className="trust-badge">
                <span className="trust-icon">🚚</span>
                <span>{shippingMethod === 'delivery' 
                  ? (shippingFee === 0 ? `Free delivery ${subtotal >= FREE_DELIVERY_THRESHOLD ? 'applied!' : `on orders $${FREE_DELIVERY_THRESHOLD.toFixed(2)}+`}` : 'Next-day delivery by 3:30 PM')
                  : 'Next-day pickup until 1:00 PM'
                }</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Processing Overlay */}
        {isProcessingPayment && (
          <div className="payment-processing-overlay">
            <div className="payment-processing-modal">
              <div className="processing-content">
                <div className="processing-animation">
                  <div className="payment-spinner">
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                    <div className="spinner-ring"></div>
                  </div>
                </div>
                
                <div className="processing-details">
                  <h3>Processing Your Payment</h3>
                  <p className="processing-step">
                    {paymentStep === 'validating' && '🔍 Validating payment information...'}
                    {paymentStep === 'processing' && '💳 Processing payment securely...'}
                    {paymentStep === 'storing' && '📝 Finalizing your order...'}
                  </p>
                  
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{width: `${paymentProgress}%`}}
                    ></div>
                  </div>
                  <p className="progress-text">{paymentProgress}% Complete</p>
                </div>
                
                <div className="processing-security">
                  <div className="security-badges">
                    <span className="security-badge">🔒 SSL Secured</span>
                    <span className="security-badge">💳 Square Payments</span>
                  </div>
                  <p className="processing-warning">
                    Please don't close this window or navigate away while we process your payment.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BucksCheckout;